import React from 'react';
import PropTypes from 'prop-types';
import SingleLine from './SingleLine';
import TextBox from './TextBox';
import MultiLineInput from './MultiLineInput';
import CheckboxQuestion from './CheckboxQuestion';
import AdditionalNotes from './AdditionalNotes';
import MultipleChoice from './MultipleChoice';
import SingleChoice from './SingleChoice';
import Attachment from './Attachment';
import Signature from './Signature';
import BoxGrid from './BoxGrid';
import Dropdown from './Dropdown';
import LinearScale from './LinearScale';
import Image from './Image';
import {FormBuilderComponentTypes} from '../ComponentList';
import OfferStatusChoice from './OfferStatusChoice';
import FileUploader from './FileUploader';
import DateTime from './DateTime';
import Date from './Date';
import AppointmentBooking from './AppointmentBooking';
import Payment from './Payment';
import LookupDropdown from './LookupDropdown';
import Address from './Address';
import IntakeChoice from './IntakeChoice';
import BoarderStatusChoice from './BoarderStatusChoice';
import NumericInputBox from './NumericInputBox';
import QuestionGrid from './QuestionGrid';
import Separator from './Separator';
import UPN from './UPN';
import PreviousSchoolName from './PreviousSchoolName';
import PreviousSchoolEmail from './PreviousSchoolEmail';

const ComponentBuilder = ({
                              index,
                              readOnly,
                              previewMode,
                              formPreviewMode,
                              handleChangeEdit,
                              handleChangeSubmit,
                              handleDownloadAttachment,
                              submitData,
                              data,
                              submitMode,
                              field,
                              isFormArchived,
                              placeholderValues,
                              accessLevel,
                              relatedComponent,
                              componentToEditIndex,
                              error,
                              audience,
                              recipientType,
                              formRequestId,
                              onEmailCheck,
                              setWarningFor,
                              warnings
                          }) => {

    switch (field.type) {
        case 'singleText': {
            const fieldId = field.id;
            const isEmailField =
                field.inputType === 'email' ||
                field.key === 'contactEmail' ||
                field.enableEmailCheck === true;
            return (
                <SingleLine
                    onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                    componentToEditIndex={componentToEditIndex}
                    index={index}
                    id={fieldId}
                    fieldKey={field.key}
                    value={readOnly ? data[fieldId] : submitData[fieldId]}
                    description={field.formattedDescription ? field.formattedDescription : field.description}
                    enableValidation={field.enableValidation}
                    accessLevel={accessLevel}
                    isEditMode={!submitMode && !previewMode}
                    isFormArchived={isFormArchived}
                    readonly={readOnly}
                    placeholderValues={placeholderValues}
                    submitMode={submitMode}
                    type={field.inputType}
                    formAccessLevel={accessLevel}
                    formRequestId={formRequestId}
                    onEmailCheck={isEmailField ? onEmailCheck : undefined}
                    warning={warnings?.[fieldId] ?? ''}
                    setWarning={(msg) => setWarningFor(fieldId, msg)}
                    error={error}
                    audience={audience}
                    recipientType={recipientType}
                />);
        }
        case 'textBox':
            return <TextBox onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                            componentToEditIndex={componentToEditIndex}
                            index={index} id={field.id} value={readOnly ? data[field.id] : submitData[field.id]}
                            description={field.formattedDescription ? field.formattedDescription : field.description}
                            enableValidation={field.enableValidation} accessLevel={accessLevel}
                            isEditMode={!submitMode && !previewMode} isFormArchived={isFormArchived}
                            readonly={readOnly} placeholderValues={placeholderValues} submitMode={submitMode}
                            error={error} audience={audience} recipientType={recipientType}
            />;
        case 'numericInputBox':
            return <NumericInputBox onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                                    componentToEditIndex={componentToEditIndex}
                                    index={index} id={field.id} value={readOnly ? data[field.id] : submitData[field.id]}
                                    priority={field.priority}
                                    description={field.formattedDescription ? field.formattedDescription : field.description}
                                    enableValidation={field.enableValidation} accessLevel={accessLevel}
                                    isEditMode={!submitMode && !previewMode} isFormArchived={isFormArchived}
                                    readonly={readOnly} placeholderValues={placeholderValues} submitMode={submitMode}
                                    error={error} showEditModeOnly={field.showEditModeOnly}
                                    fieldKey={field.fieldKey ?? field.key} audience={audience}
                                    recipientType={recipientType}
            />;
        case 'multiLineInput':
            return <MultiLineInput onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                                   componentToEditIndex={componentToEditIndex}
                                   index={index} id={field.id} fieldKey={field.key}
                                   value={readOnly ? data[field.id] : submitData[field.id]}
                                   description={field.formattedDescription ? field.formattedDescription : field.description}
                                   enableValidation={field.enableValidation} accessLevel={accessLevel}
                                   isEditMode={!submitMode && !previewMode} isFormArchived={isFormArchived}
                                   readonly={readOnly} placeholderValues={placeholderValues} submitMode={submitMode}
                                   inputs={field.inputs} error={error} audience={audience} recipientType={recipientType}
            />;
        case 'appointmentBooking':
            return <AppointmentBooking onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                                       index={index} id={field.id} readOnly={readOnly} accessLevel={accessLevel}
                                       description={field.formattedDescription ? field.formattedDescription : field.description}
                                       bookingLink={field.bookingLink}
                                       enableValidation={field.enableValidation}
                                       isEditMode={!submitMode && !previewMode} isFormArchived={isFormArchived}
                                       placeholderValues={placeholderValues} componentToEditIndex={componentToEditIndex}
                                       audience={audience} recipientType={recipientType} submitMode={submitMode}
            />;
        case 'payment':
            return <Payment onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                            index={index} id={field.id} readOnly={readOnly} accessLevel={accessLevel}
                            description={field.formattedDescription ? field.formattedDescription : field.description}
                            paymentLink={field.paymentLink}
                            enableValidation={field.enableValidation}
                            isEditMode={!submitMode && !previewMode} isFormArchived={isFormArchived}
                            placeholderValues={placeholderValues} componentToEditIndex={componentToEditIndex}
                            audience={audience} recipientType={recipientType} submitMode={submitMode}
            />;
        case 'tickBoxGrid':
        case 'multipleChoiceGrid':
            return <BoxGrid onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                            componentToEditIndex={componentToEditIndex}
                            index={index} id={field.id} value={readOnly ? data[field.id] : submitData[field.id]}
                            description={field.formattedDescription ? field.formattedDescription : field.description}
                            enableValidation={field.enableValidation} accessLevel={accessLevel}
                            isEditMode={!submitMode && !previewMode} isFormArchived={isFormArchived} rows={field.rows}
                            columns={field.columns} readOnly={readOnly} type={field.type}
                            readonly={readOnly} placeholderValues={placeholderValues} audience={audience}
                            recipientType={recipientType}
                            error={error} submitMode={submitMode}

            />;
        case 'checkboxQuestion':
            return <CheckboxQuestion onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                                     componentToEditIndex={componentToEditIndex}
                                     index={index} id={field.id} accessLevel={accessLevel}
                                     value={readOnly ? data[field.id] : submitData[field.id]}
                                     description={field.formattedDescription ? field.formattedDescription : field.description}
                                     enableValidation={field.enableValidation} submitMode={submitMode}
                                     isEditMode={!submitMode && !previewMode} isFormArchived={isFormArchived}
                                     readonly={readOnly} placeholderValues={placeholderValues}
                                     error={error} audience={audience} recipientType={recipientType}
            />;
        case 'additionalNotes':
            return <AdditionalNotes onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                                    index={index} id={field.id} readOnly={readOnly} accessLevel={accessLevel}
                                    description={field.formattedDescription ? field.formattedDescription : field.description}
                                    enableValidation={field.enableValidation} submitMode={submitMode}
                                    isEditMode={!submitMode && !previewMode} isFormArchived={isFormArchived}
                                    placeholderValues={placeholderValues} componentToEditIndex={componentToEditIndex}
                                    audience={audience} recipientType={recipientType}
            />;
        case 'multipleChoice':
            return <MultipleChoice onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                                   componentToEditIndex={componentToEditIndex}
                                   index={index} id={field.id} fieldKey={field.key} options={field.options}
                                   value={readOnly ? data[field.id] : submitData[field.id]}
                                   description={field.formattedDescription ? field.formattedDescription : field.description}
                                   enableValidation={field.enableValidation} submitMode={submitMode}
                                   isEditMode={!submitMode && !previewMode} isFormArchived={isFormArchived}
                                   relatedComponent={relatedComponent} accessLevel={accessLevel}
                                   readonly={readOnly} placeholderValues={placeholderValues}
                                   error={error} audience={audience} recipientType={recipientType}
            />;
        case 'singleChoice':
            return <SingleChoice onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                                 componentToEditIndex={componentToEditIndex}
                                 index={index} id={field.id} fieldKey={field.key} options={field.options}
                                 value={readOnly ? data[field.id] : submitData[field.id]}
                                 description={field.formattedDescription ? field.formattedDescription : field.description}
                                 enableValidation={field.enableValidation} submitMode={submitMode}
                                 isEditMode={!submitMode && !previewMode} isFormArchived={isFormArchived}
                                 relatedComponent={relatedComponent} accessLevel={accessLevel}
                                 readonly={readOnly} placeholderValues={placeholderValues} error={error}
                                 audience={audience} recipientType={recipientType}
            />;
        case 'linearScale':
            return <LinearScale onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                                componentToEditIndex={componentToEditIndex}
                                index={index} id={field.id} startFrom={field.startFrom} endAt={field.endAt}
                                startLabel={field.startLabel} endLabel={field.endLabel}
                                value={readOnly ? data[field.id] : submitData[field.id]}
                                description={field.formattedDescription ? field.formattedDescription : field.description}
                                enableValidation={field.enableValidation} submitMode={submitMode}
                                isEditMode={!submitMode && !previewMode} isFormArchived={isFormArchived}
                                isSubmitted={readOnly && submitMode} accessLevel={accessLevel}
                                readonly={readOnly} placeholderValues={placeholderValues} audience={audience}
                                recipientType={recipientType}
            />;
        case 'dropdown':
            return <Dropdown onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                             componentToEditIndex={componentToEditIndex}
                             index={index} id={field.id} options={field.options}
                             value={readOnly ? data[field.id] : submitData[field.id]}
                             description={field.formattedDescription ? field.formattedDescription : field.description}
                             enableValidation={field.enableValidation} submitMode={submitMode}
                             isEditMode={!submitMode && !previewMode} isFormArchived={isFormArchived}
                             relatedComponent={relatedComponent} accessLevel={accessLevel}
                             readonly={readOnly} placeholderValues={placeholderValues}
                             error={error} audience={audience} recipientType={recipientType}
            />;
        case 'lookupDropdown':
            return <LookupDropdown onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                                   componentToEditIndex={componentToEditIndex}
                                   index={index} id={field.id} lookupType={field.lookupType} fieldKey={field.key}
                                   value={readOnly ? data[field.id] : submitData[field.id]}
                                   description={field.formattedDescription ? field.formattedDescription : field.description}
                                   enableValidation={field.enableValidation} submitMode={submitMode}
                                   isEditMode={!submitMode && !previewMode} isFormArchived={isFormArchived}
                                   readonly={readOnly} placeholderValues={placeholderValues}
                                   error={error} audience={audience} recipientType={recipientType}
            />;
        case 'attachment':
            return <Attachment id={field.id}
                               onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                               componentToEditIndex={componentToEditIndex}
                               index={index} submitMode={submitMode}
                               description={field.formattedDescription ? field.formattedDescription : field.description}
                               attachments={field.attachments} accessLevel={accessLevel}
                               isEditMode={!submitMode && !previewMode} isFormArchived={isFormArchived}
                               readonly={readOnly} placeholderValues={placeholderValues} audience={audience}
                               recipientType={recipientType}
            />;
        case 'image':
            return <Image id={field.id} onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                          componentToEditIndex={componentToEditIndex}
                          index={index} submitMode={submitMode}
                          description={field.formattedDescription ? field.formattedDescription : field.description}
                          attachments={field.attachments} accessLevel={accessLevel}
                          isEditMode={!submitMode && !previewMode} isFormArchived={isFormArchived}
                          readonly={readOnly} placeholderValues={placeholderValues} audience={audience}
                          recipientType={recipientType}
            />;
        case 'signature':
            return <Signature onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                              id={field.id} value={readOnly ? data[field.id] : submitData[field.id]}
                              isEditMode={!submitMode && !previewMode} isSubmitted={readOnly && submitMode}
                              error={error}
            />;
        case FormBuilderComponentTypes.OfferStatusChoice:
            return <OfferStatusChoice onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                                      index={index} id={field.id} options={field.options}
                                      value={readOnly ? data[field.id] : submitData[field.id]}
                                      description={field.formattedDescription ? field.formattedDescription : field.description}
                                      enableValidation={field.enableValidation} submitMode={submitMode}
                                      componentToEditIndex={componentToEditIndex}
                                      isEditMode={!submitMode && !previewMode} isFormArchived={isFormArchived}
                                      relatedComponent={relatedComponent} accessLevel={accessLevel}
                                      readonly={readOnly} placeholderValues={placeholderValues}
                                      error={error} audience={audience} recipientType={recipientType}
            />;
        case FormBuilderComponentTypes.FileUploader:
            return <FileUploader
                key={`FileUploader_${field.id}`}
                id={field.id}
                index={index}
                value={readOnly ? data[field.id] : submitData[field.id]}
                description={field.formattedDescription ? field.formattedDescription : field.description}
                onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                isFormArchived={isFormArchived}
                accessLevel={accessLevel}
                isEditMode={!submitMode && !previewMode}
                readonly={readOnly}
                placeholderValues={placeholderValues}
                componentToEditIndex={componentToEditIndex}
                enableValidation={field.enableValidation}
                handleDownloadAttachment={handleDownloadAttachment}
                error={error}
                submitMode={submitMode}
                audience={audience}
                recipientType={recipientType}
            />;
        case 'dateTime':
            return <DateTime onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                             componentToEditIndex={componentToEditIndex}
                             index={index} id={field.id} value={readOnly ? data[field.id] : submitData[field.id]}
                             description={field.formattedDescription ? field.formattedDescription : field.description}
                             enableValidation={field.enableValidation} accessLevel={accessLevel}
                             isEditMode={!submitMode && !previewMode} isFormArchived={isFormArchived}
                             readonly={readOnly} placeholderValues={placeholderValues} submitMode={submitMode}
                             minDateTime={field.minDateTime} maxDateTime={field.maxDateTime} error={error}
                             audience={audience} recipientType={recipientType}
            />;
        case 'date':
            return <Date onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                         componentToEditIndex={componentToEditIndex}
                         index={index} id={field.id} fieldKey={field.key}
                         value={readOnly ? data[field.id] : submitData[field.id]}
                         description={field.formattedDescription ? field.formattedDescription : field.description}
                         enableValidation={field.enableValidation} accessLevel={accessLevel}
                         isEditMode={!submitMode && !previewMode} isFormArchived={isFormArchived}
                         readonly={readOnly} placeholderValues={placeholderValues} submitMode={submitMode}
                         minAge={field.minAge} error={error} audience={audience} recipientType={recipientType}
            />;
        case FormBuilderComponentTypes.Address:
            return <Address
                onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                componentToEditIndex={componentToEditIndex}
                index={index} submitMode={submitMode}
                id={field.id}
                value={readOnly ? data[field.id] : submitData[field.id]}
                description={field.formattedDescription ? field.formattedDescription : field.description}
                enableValidation={field.enableValidation}
                isEditMode={!submitMode && !previewMode}
                isFormArchived={isFormArchived}
                readonly={readOnly}
                placeholderValues={placeholderValues}
                accessLevel={accessLevel}
                error={error}
                audience={audience}
                recipientType={recipientType}
            />;
        case FormBuilderComponentTypes.IntakeChoice:
            return <IntakeChoice
                onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                index={index} submitMode={submitMode}
                id={field.id}
                fieldKey={field.key}
                options={field.options}
                value={readOnly ? data[field.id] : submitData[field.id]}
                description={field.formattedDescription ? field.formattedDescription : field.description}
                enableValidation={field.enableValidation}
                componentToEditIndex={componentToEditIndex}
                isEditMode={!submitMode && !previewMode}
                isSubmitMode={submitMode}
                isFormPreviewMode={formPreviewMode}
                isFormArchived={isFormArchived}
                relatedComponent={relatedComponent}
                accessLevel={accessLevel}
                readonly={readOnly}
                placeholderValues={placeholderValues}
                applicationStatusId={field.applicationStatusId}
                error={error}
                audience={audience}
                recipientType={recipientType}
            />;
        case FormBuilderComponentTypes.BoarderStatusChoice:
            return <BoarderStatusChoice
                onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                index={index}
                id={field.id} submitMode={submitMode}
                fieldKey={field.key}
                options={field.options}
                value={readOnly ? data[field.id] : submitData[field.id]}
                description={field.formattedDescription ? field.formattedDescription : field.description}
                enableValidation={field.enableValidation}
                componentToEditIndex={componentToEditIndex}
                isEditMode={!submitMode && !previewMode}
                isSubmitMode={submitMode}
                isFormPreviewMode={formPreviewMode}
                isFormArchived={isFormArchived}
                relatedComponent={relatedComponent}
                accessLevel={accessLevel}
                readonly={readOnly}
                placeholderValues={placeholderValues}
                error={error}
                audience={audience}
                recipientType={recipientType}
            />;
        case FormBuilderComponentTypes.QuestionGrid:
            return <QuestionGrid
                onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                componentToEditIndex={componentToEditIndex}
                index={index}
                id={field.id}
                value={readOnly ? data[field.id] : submitData[field.id]}
                description={field.formattedDescription ? field.formattedDescription : field.description}
                isEditMode={!submitMode && !previewMode}
                isFormArchived={isFormArchived}
                readonly={readOnly}
                placeholderValues={placeholderValues}
                accessLevel={accessLevel}
                inputs={field.inputs}
                columns={field.columns}
                error={error}
                submitMode={submitMode}
                audience={audience}
                recipientType={recipientType}
            />;
        case FormBuilderComponentTypes.Separator:
            return <Separator
                key={`separator-${field.id}`}
                field={field}
                isEditMode={!submitMode && !previewMode}
                submitMode={submitMode}
                readOnly={readOnly}
                onChange={handleChangeEdit}
                index={index}
                componentToEditIndex={componentToEditIndex}
            />;
        case FormBuilderComponentTypes.UPN:
            return <UPN
                onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                componentToEditIndex={componentToEditIndex}
                index={index}
                id={field.id}
                fieldKey={field.key}
                value={readOnly ? data[field.id] : submitData[field.id]}
                description={field.formattedDescription ? field.formattedDescription : field.description}
                enableValidation={field.enableValidation}
                isEditMode={!submitMode && !previewMode}
                isFormArchived={isFormArchived}
                readonly={readOnly}
                placeholderValues={placeholderValues}
                submitMode={submitMode}
                accessLevel={accessLevel}
                error={error}
                audience={audience}
                recipientType={recipientType}
            />;
        case FormBuilderComponentTypes.PreviousSchoolName:
            return <PreviousSchoolName
                onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                componentToEditIndex={componentToEditIndex}
                index={index}
                id={field.id}
                fieldKey={field.key}
                value={readOnly ? data[field.id] : submitData[field.id]}
                description={field.formattedDescription ? field.formattedDescription : field.description}
                enableValidation={field.enableValidation}
                isEditMode={!submitMode && !previewMode}
                isFormArchived={isFormArchived}
                readonly={readOnly}
                placeholderValues={placeholderValues}
                submitMode={submitMode}
                accessLevel={accessLevel}
                error={error}
                audience={audience}
                recipientType={recipientType}
            />;
        case FormBuilderComponentTypes.PreviousSchoolEmail:
            return <PreviousSchoolEmail
                onChange={!submitMode && !previewMode ? handleChangeEdit : handleChangeSubmit}
                componentToEditIndex={componentToEditIndex}
                index={index}
                id={field.id}
                fieldKey={field.key}
                value={readOnly ? data[field.id] : submitData[field.id]}
                description={field.formattedDescription ? field.formattedDescription : field.description}
                enableValidation={field.enableValidation}
                isEditMode={!submitMode && !previewMode}
                isFormArchived={isFormArchived}
                readonly={readOnly}
                placeholderValues={placeholderValues}
                submitMode={submitMode}
                accessLevel={accessLevel}
                error={error}
                audience={audience}
                recipientType={recipientType}
            />;
        default:
            return null;
    }
};

ComponentBuilder.propTypes = {
    index: PropTypes.number,
    readOnly: PropTypes.bool,
    previewMode: PropTypes.bool,
    formPreviewMode: PropTypes.bool,
    handleChangeEdit: PropTypes.func,
    handleChangeSubmit: PropTypes.func,
    handleDownloadAttachment: PropTypes.func,
    submitData: PropTypes.object,
    data: PropTypes.object,
    submitMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    field: PropTypes.object,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    relatedComponent: PropTypes.object,
    componentToEditIndex: PropTypes.number,
    error: PropTypes.any,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    formRequestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onEmailCheck: PropTypes.func,
    formAccessLevel: PropTypes.number,
    warnings: PropTypes.string,
    setWarningFor: PropTypes.func
};

export default ComponentBuilder;